import { useState } from 'react';
import { ROLE_LABELS_FA, ROLES, type Role } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import { Button, Card, ErrorRetry, Field, Input, useConfirm, useToast } from '../ui';

interface UserRow {
  id: number;
  username: string;
  role: Role;
  name: string;
  active: number;
}

export function UsersScreen() {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, status, error: loadError, reload } = useApiResource<UserRow[]>('/api/users');
  const list = data ?? [];
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('Sales');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function setActive(u: UserRow, active: boolean) {
    if (
      !active &&
      !(await confirm({
        title: 'غیرفعال‌سازی کاربر',
        message: `«${u.name}» غیرفعال شود؟ دیگر نمی‌تواند وارد شود.`,
        confirmLabel: 'غیرفعال کن',
        danger: true,
      }))
    )
      return;
    const r = await api<UserRow>(`/api/users/${u.id}/active`, {
      method: 'POST',
      body: JSON.stringify({ active }),
    });
    if (!r.ok) return toast(errMsg(r), 'error');
    toast(active ? 'کاربر فعال شد' : 'کاربر غیرفعال شد');
    reload();
  }

  async function changeRole(u: UserRow, next: Role) {
    if (next === u.role) return;
    const r = await api<UserRow>(`/api/users/${u.id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role: next }),
    });
    if (!r.ok) toast(errMsg(r), 'error');
    else toast(`نقش «${u.name}» به ${ROLE_LABELS_FA[next]} تغییر کرد`);
    reload(); // on failure this reverts the dropdown to the true role
  }

  async function resetPass(u: UserRow) {
    // A strong temporary password the admin hands over; the user rotates it via «تغییر گذرواژه».
    const bytes = crypto.getRandomValues(new Uint8Array(9));
    const temp = 'Kia-' + Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('');
    if (
      !(await confirm({
        title: `بازنشانی گذرواژهٔ «${u.name}»`,
        message: `گذرواژهٔ موقت: ${temp} — همین حالا یادداشت کنید و به کاربر بدهید (پس از بستن دوباره نشان داده نمی‌شود). کاربر می‌تواند بعداً آن را از «تغییر گذرواژه» عوض کند.`,
        confirmLabel: 'بازنشانی',
      }))
    )
      return;
    const r = await api(`/api/users/${u.id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword: temp }),
    });
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('گذرواژه بازنشانی شد');
  }

  async function add() {
    setError('');
    if (!username.trim() || !password) return setError('نام کاربری و گذرواژه لازم است');
    setBusy(true);
    const r = await api('/api/users', {
      method: 'POST',
      body: JSON.stringify({
        name: name.trim() || username.trim(),
        username: username.trim(),
        password,
        role,
      }),
    });
    setBusy(false);
    if (r.status === 409) return setError('این نام کاربری قبلاً وجود دارد');
    if (!r.ok) return setError(errMsg(r));
    toast('کاربر افزوده شد');
    setName('');
    setUsername('');
    setPassword('');
    reload();
  }

  return (
    <>
      <Card title="افزودن کاربر">
        <div className="form-grid">
          <Field label="نام">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="نام کاربری">
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <Field label="گذرواژه" hint="حداقل ۸ نویسه">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Field label="نقش">
            <select
              className="input"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS_FA[r]}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {error ? <div className="field-error">{error}</div> : null}
        <div className="row" style={{ marginTop: 12 }}>
          <Button onClick={add} loading={busy}>
            افزودن
          </Button>
        </div>
      </Card>

      <Card title={`کاربران (${list.length})`}>
        {status === 'error' ? (
          <ErrorRetry error={loadError} onRetry={() => reload()} />
        ) : status === 'loading' ? (
          <p className="muted">در حال بارگذاری…</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>نام</th>
                  <th>نام کاربری</th>
                  <th>نقش</th>
                  <th>وضعیت</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td className="empty" colSpan={5}>
                      هنوز کاربری ثبت نشده — از فرم بالا اضافه کنید.
                    </td>
                  </tr>
                ) : (
                  list.map((u) => (
                    <tr key={u.id}>
                      <td>{u.name}</td>
                      <td>{u.username}</td>
                      <td>
                        <select
                          className="input"
                          value={u.role}
                          onChange={(e) => void changeRole(u, e.target.value as Role)}
                          style={{ maxWidth: 130 }}
                          aria-label={`نقش ${u.name}`}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABELS_FA[r]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ color: u.active ? 'var(--green)' : 'var(--red)' }}>
                        {u.active ? 'فعال' : 'غیرفعال'}
                      </td>
                      <td>
                        <Button variant="ghost" onClick={() => void resetPass(u)}>
                          بازنشانی گذرواژه
                        </Button>
                        {u.active ? (
                          <Button variant="ghost" onClick={() => void setActive(u, false)}>
                            غیرفعال‌سازی
                          </Button>
                        ) : (
                          <Button variant="ghost" onClick={() => void setActive(u, true)}>
                            فعال‌سازی
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
